// Documents → a person's Drive folder, read live: 5 - Staff Folder / <E-number>,
// including anything filed there by hand. The same view a client's Files tab
// has. The staff_documents list below it keeps what Drive cannot show: who
// signed what, and when.
import { useCallback, useEffect, useMemo, useState } from 'react';
import OpenLink from '../OpenLink';
import { FileRow, FolderRow } from '../DriveFolderTree';
import { useAuth } from '../../hooks/useAuth';
import { driveFolderUrl, STAFF_DOC_BUCKETS } from '../../lib/driveRoots';
import { driveFileId } from '../../lib/driveLinks';
import { fileApplicationDocuments, filesUnder, listStaffFolder, uploadToStaffFolder } from '../../lib/driveListing';
import { logOnboardingEvent } from '../../lib/staffCompliance';

const linkBtn = { background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 12, color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline' };
const box = { border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', background: 'var(--card-bg)' };
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

export default function StaffFilesPanel({ staff, documents = [], onChanged }) {
  const { profile } = useAuth();
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(() => new Set());
  const [upload, setUpload] = useState(null);       // { folder, locked } while the upload window is open
  const [moving, setMoving] = useState(false);
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try { setTree(await listStaffFolder(staff.id)); }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [staff.id]);
  useEffect(() => { load(); }, [load]);

  // What CAERA knows about a file: its signature, or what it is the record of.
  const byFile = useMemo(() => {
    const m = new Map();
    documents.forEach((d) => { const id = driveFileId(d.pdf_url); if (id && !m.has(id)) m.set(id, d); });
    return m;
  }, [documents]);
  const fileNotes = useCallback((f) => {
    const d = byFile.get(f.id);
    if (!d) return null;
    return <span style={{ color: 'var(--text2)' }}>· {d.signed_name ? `Signed by ${d.signed_name}` : (d.doc_label || d.doc_type)}</span>;
  }, [byFile]);

  const toggle = (id) => setOpen((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const folders = [...(tree?.folders || [])].sort((a, b) => a.name.localeCompare(b.name));
  const total = folders.reduce((n, f) => n + filesUnder(f).length, 0) + (tree?.files?.length || 0);
  const waiting = tree?.waiting || [];

  const bringIn = async () => {
    setMoving(true); setErr(''); setNotice('');
    try {
      const { moved, failed } = await fileApplicationDocuments(staff.id);
      setNotice(`${plural(moved.length, 'document')} moved to Onboarding.${failed.length ? ` Could not move: ${failed.join(', ')}.` : ''}`);
      await load();
      onChanged?.();
    } catch (e) { setErr(e.message); }
    finally { setMoving(false); }
  };

  return (
    <div style={{ display: 'grid', gap: 10, marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{staff.first_name}'s files</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
            The Drive folder, read live{staff.employee_id ? ` · 5 – Staff Folder / ${staff.employee_id}` : ''}{tree?.folderId ? ` · ${plural(total, 'file')}` : ''} ·{' '}
            <button type="button" style={linkBtn} onClick={load} disabled={loading}>{loading ? 'Reading…' : 'Refresh'}</button>
          </div>
        </div>
        {tree?.folderId && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-outline btn-sm" onClick={() => setUpload({ folder: 'Needs Review', locked: false })}>Upload</button>
            <OpenLink href={driveFolderUrl(tree.folderId)}>Open in Drive</OpenLink>
          </div>
        )}
      </div>

      {waiting.length > 0 && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '10px 14px', borderRadius: 8, fontSize: 12.5, color: 'var(--warn)', background: 'var(--warn-bg)', border: '1px solid var(--warn-border)' }}>
          <span style={{ flex: 1, minWidth: 220 }}>{plural(waiting.length, 'document')} from {staff.first_name}'s application {waiting.length === 1 ? 'is' : 'are'} still in Staff Applications: {waiting.map((w) => w.label).join(', ')}.</span>
          <button className="btn btn-outline btn-sm" onClick={bringIn} disabled={moving}>{moving ? 'Moving…' : 'Move here'}</button>
        </div>
      )}
      {notice && <div style={{ fontSize: 12, color: 'var(--success)' }}>{notice}</div>}

      <div style={box}>
        {err && <div style={{ color: 'var(--danger)', fontSize: 12.5, padding: '12px 16px' }}>{err}</div>}
        {!err && loading && !tree && <div style={{ fontSize: 13, color: 'var(--text3)', padding: '12px 16px' }}>Reading the Drive folder…</div>}
        {!err && tree && !tree.folderId && (
          <div style={{ fontSize: 13, color: 'var(--text3)', padding: '12px 16px' }}>No folder yet. It is created when {staff.first_name} gets an E-number; until then their documents sit with their application.</div>
        )}
        {tree?.folderId && folders.length === 0 && !tree.files?.length && (
          <div style={{ fontSize: 13, color: 'var(--text3)', padding: '12px 16px' }}>The folder is empty.</div>
        )}
        {folders.map((folder) => (
          <FolderRow key={folder.id} folder={folder} open={open.has(folder.id)} onToggle={() => toggle(folder.id)}
            fileNotes={fileNotes} onUpload={(path) => setUpload({ folder: path, locked: true })} />
        ))}
        {tree?.files?.map((f) => <FileRow key={f.id} file={f} fileNotes={fileNotes} indent={16} />)}
      </div>

      {upload && (
        <UploadModal staff={staff} initialFolder={upload.folder} locked={upload.locked} onClose={() => setUpload(null)}
          onDone={async (names, folder) => {
            setUpload(null);
            setNotice(`${plural(names.length, 'file')} added to ${folder}.`);
            await logOnboardingEvent(staff.id, 'document_uploaded', `${names.join(', ')} uploaded to ${folder} by ${profile?.full_name || 'office'}`, null, null, profile?.full_name);
            await load();
            onChanged?.();
          }} />
      )}
    </div>
  );
}

function UploadModal({ staff, initialFolder, locked, onClose, onDone }) {
  const [folder, setFolder] = useState(initialFolder);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const send = async () => {
    setBusy(true); setError('');
    try {
      for (const file of files) await uploadToStaffFolder(staff.id, folder, file);
      await onDone(files.map((f) => f.name), folder);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <div className="modal-title">Upload to {staff.employee_id}</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}>✕</button>
        </div>
        <div className="modal-body">
          {error && <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--danger)' }}>{error}</div>}
          <div className="form-group">
            <label className="form-label">Folder</label>
            {locked
              ? <div style={{ fontSize: 13, color: 'var(--text)' }}>{folder}</div>
              : <select className="form-select" value={folder} onChange={(e) => setFolder(e.target.value)}>{STAFF_DOC_BUCKETS.map((b) => <option key={b} value={b}>{b}</option>)}</select>}
          </div>
          <div className="form-group">
            <label className="form-label">Files</label>
            <input type="file" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} style={{ fontSize: 12 }} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={send} disabled={busy || !files.length}>{busy ? 'Uploading…' : 'Upload'}</button>
        </div>
      </div>
    </div>
  );
}
